import React from 'react';
import { cn } from '../../utils/cn';
import type { HeadingProps } from '../../types/components';

const headingSizes = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
  '7xl': 'text-7xl',
  '8xl': 'text-8xl',
  '9xl': 'text-9xl',
};

const headingWeights = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const headingAligns = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

// Default sizes for each heading level (responsive)
const defaultSizes = {
  1: '5xl', // H1: Large and impactful
  2: '4xl', // H2: Section headers
  3: '2xl', // H3: Subsection headers
  4: 'xl',  // H4: Minor headers
  5: 'lg',  // H5: Labels
  6: 'base', // H6: Smallest
} as const;

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      level = 1,
      size,
      weight = 'bold',
      color,
      align = 'left',
      truncate = false,
      children,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const Tag = `h${level}` as keyof JSX.IntrinsicElements;
    
    // Use provided size or default for the heading level
    const headingSize = size || defaultSizes[level];
    
    const baseClasses = [
      'font-sans',
      'leading-tight',
    ];

    const sizeClass = headingSizes[headingSize] || headingSizes['2xl'];
    const weightClass = headingWeights[weight] || headingWeights.bold;
    const alignClass = headingAligns[align] || headingAligns.left;

    const classes = cn(
      baseClasses,
      sizeClass,
      weightClass,
      alignClass,
      {
        'truncate': truncate,
        'text-gray-900': !color,
      },
      className
    );

    const style = color ? { color } : undefined;

    return React.createElement(
      Tag,
      {
        ref,
        className: classes,
        style,
        'data-testid': testId,
        ...props,
      },
      children
    );
  }
);

Heading.displayName = 'Heading';